<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store.js';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  boardId: {
    type: Number,
    required: true,
  },
  steps: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSaving = ref(false);
const editingTask = ref(null);

const form = ref({
  title: '',
  description: '',
  board_step_id: null,
  priority: '',
  due_date: '',
  value: 0,
});

const isEditing = computed(() => Boolean(editingTask.value));

const priorityOptions = computed(() => [
  { value: '', label: t('KANBAN.TASK.PRIORITY.NONE') },
  { value: 'low', label: t('KANBAN.TASK.PRIORITY.LOW') },
  { value: 'medium', label: t('KANBAN.TASK.PRIORITY.MEDIUM') },
  { value: 'high', label: t('KANBAN.TASK.PRIORITY.HIGH') },
  { value: 'urgent', label: t('KANBAN.TASK.PRIORITY.URGENT') },
]);

const toDatetimeLocal = value => {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const open = (task = null, stepId = null) => {
  editingTask.value = task;
  form.value = {
    title: task?.title || '',
    description: task?.description || '',
    board_step_id: task?.board_step_id || stepId || props.steps[0]?.id,
    priority: task?.priority || '',
    due_date: toDatetimeLocal(task?.due_date),
    value: Number(task?.value) || 0,
  };
  dialogRef.value?.open();
};

const onConfirm = async () => {
  if (!form.value.title.trim()) return;

  isSaving.value = true;
  const payload = {
    title: form.value.title.trim(),
    description: form.value.description,
    board_step_id: form.value.board_step_id,
    priority: form.value.priority || null,
    due_date: form.value.due_date
      ? new Date(form.value.due_date).toISOString()
      : null,
    value: form.value.value,
  };

  try {
    if (isEditing.value) {
      await store.dispatch('kanban/updateTask', {
        taskId: editingTask.value.id,
        ...payload,
      });
    } else {
      await store.dispatch('kanban/createTask', {
        ...payload,
        board_id: props.boardId,
      });
    }
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.message);
  } finally {
    isSaving.value = false;
  }
};

const onDelete = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.TASK.DELETE_CONFIRM'))) return;

  try {
    await store.dispatch('kanban/deleteTask', editingTask.value.id);
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.message);
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing ? t('KANBAN.TASK.EDIT_TITLE') : t('KANBAN.TASK.NEW_TITLE')
    "
    :confirm-button-label="t('KANBAN.TASK.SAVE')"
    :is-loading="isSaving"
    :disable-confirm-button="!form.title.trim()"
    overflow-y-auto
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.title"
        :label="t('KANBAN.TASK.TITLE_LABEL')"
        :placeholder="t('KANBAN.TASK.TITLE_PLACEHOLDER')"
      />
      <TextArea
        v-model="form.description"
        :label="t('KANBAN.TASK.DESCRIPTION_LABEL')"
      />

      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('KANBAN.TASK.STEP_LABEL') }}
          <select
            v-model="form.board_step_id"
            class="px-2 py-1.5 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
          >
            <option v-for="step in steps" :key="step.id" :value="step.id">
              {{ step.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('KANBAN.TASK.PRIORITY_LABEL') }}
          <select
            v-model="form.priority"
            class="px-2 py-1.5 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
          >
            <option
              v-for="option in priorityOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('KANBAN.TASK.DUE_DATE_LABEL') }}
          <input
            v-model="form.due_date"
            type="datetime-local"
            class="px-2 py-1.5 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
          />
        </label>

        <Input
          v-model="form.value"
          type="number"
          :label="t('KANBAN.TASK.VALUE_LABEL')"
        />
      </div>

      <Button
        v-if="isEditing"
        ghost
        ruby
        sm
        icon="i-lucide-trash-2"
        class="self-start"
        @click="onDelete"
      >
        {{ t('KANBAN.TASK.DELETE') }}
      </Button>
    </div>
  </Dialog>
</template>
